import UnsavedChangesModal from "@components/UnsavedChangesModal";
import Path from "@core/FileProvider/Path/Path";
import type { ResourceError } from "@core-ui/ContextServices/ResourceService/errors";
import { useGetResource } from "@core-ui/ContextServices/ResourceService/hooks/useGetResource";
import ResourceService from "@core-ui/ContextServices/ResourceService/ResourceService";
import useModalHistoryEntry from "@core-ui/hooks/useModalHistoryEntry";
import ErrorConfirmService from "@ext/errorHandlers/client/ErrorConfirmService";
import DefaultError from "@ext/errorHandlers/logic/DefaultError";
import t from "@ext/localization/locale/translate";
import DiagramError from "@ext/markdown/elements/diagrams/component/DiagramError";
import type { BpmnModelerInstance } from "@ext/markdown/elements/diagrams/diagrams/bpmn/BpmnModelerCanvas";
import getBpmnDiagram from "@ext/markdown/elements/diagrams/diagrams/bpmn/getBpmnDiagram";
import getNaturalSize from "@ext/markdown/elements/diagrams/logic/getNaturalSize";
import type { Editor } from "@tiptap/core";
import { Button } from "@ui-kit/Button";
import { FormDescription, FormHeaderBase, FormTitle } from "@ui-kit/Form";
import { FeatureIcon } from "@ui-kit/Icon";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";

interface BpmnEditorProps {
	editor: Editor;
	src: string;
	onClose?: () => void;
}

/** bpmn-js пишет это в файл как автора последней правки. */
const EXPORTER = "Gramax";

// Моделер со стилями — 166 КБ gzip.
const LazyBpmnModelerCanvas = lazy(() => import("@ext/markdown/elements/diagrams/diagrams/bpmn/BpmnModelerCanvas"));

/** Escape не закрывает редактор: в моделере им отменяют ввод подписи и перетаскивание. */
const BpmnEditor = ({ editor, src, onClose }: BpmnEditorProps) => {
	const { setResource } = ResourceService.value;
	const modelerRef = useRef<BpmnModelerInstance>(null);
	const [hasChanges, setHasChanges] = useState(false);
	const [showWarning, setShowWarning] = useState(false);
	const [initialXml, setInitialXml] = useState<string>(null);
	const [loadError, setLoadError] = useState<Error>(null);

	const onResource = useCallback((buffer: Buffer, resourceError?: ResourceError) => {
		if (resourceError) return setLoadError(resourceError);
		// Перечитать файл после «Сохранить» значит сбросить масштаб, выделение и историю отмен.
		setInitialXml((loaded) => loaded ?? buffer.toString());
	}, []);

	useGetResource(onResource, src);

	const onReady = useCallback((modeler: BpmnModelerInstance) => {
		modelerRef.current = modeler;
	}, []);

	const onChange = useCallback(() => setHasChanges(true), []);

	const save = useCallback(async (): Promise<boolean> => {
		const modeler = modelerRef.current;
		if (!modeler) return false;

		modeler.getDefinitions().set("exporter", EXPORTER);
		const { xml } = await modeler.saveXML({ format: true });

		const saved = await setResource(src, xml, undefined, true);
		if (!saved) {
			ErrorConfirmService.notify(new DefaultError(t("diagram.error.save-failed")));
			return false;
		}

		setHasChanges(false);

		const { width, height } = getNaturalSize(await getBpmnDiagram(xml));
		if (width && height) {
			editor.commands.updateAttributes("diagrams", { width: `${width}px`, height: `${height}px` });
		}
		return true;
	}, [editor, setResource, src]);

	const exit = useCallback(() => onClose?.(), [onClose]);

	const saveAndExit = useCallback(async () => {
		if (await save()) exit();
	}, [save, exit]);

	const requestExit = useCallback(() => {
		if (hasChanges) return setShowWarning(true);
		exit();
	}, [hasChanges, exit]);

	// Пока редактор открыт, «Назад» выходит из него, а не из статьи.
	const { claim: claimHistoryEntry } = useModalHistoryEntry(requestExit);

	useEffect(() => claimHistoryEntry(), [claimHistoryEntry]);

	const stay = useCallback(() => {
		setShowWarning(false);
		claimHistoryEntry();
	}, [claimHistoryEntry]);

	return (
		<>
			<div
				className="fixed inset-0 z-[var(--z-index-ui-kit-modal)] flex flex-col bg-primary-bg"
				data-modal-root
				data-qa="qa-bpmn-editor"
			>
				<FormHeaderBase className="flex items-center justify-between gap-4">
					<div className="flex min-w-0 items-center gap-3">
						<FeatureIcon className="flex-shrink-0" icon="bpmn" size="lg" />
						<div className="flex min-w-0 flex-col gap-0.5">
							<FormTitle>{t("diagram.bpmn-editor")}</FormTitle>
							{/* Имя файла — единственное, что отличает одну схему статьи от другой. */}
							<FormDescription className="truncate">{new Path(src).nameWithExtension}</FormDescription>
						</div>
					</div>
					<div className="flex items-center gap-2">
						<Button onClick={requestExit} variant="outline">
							{t("diagram.modal.warning-have-changes.exit")}
						</Button>
						<Button disabled={!initialXml} onClick={save} variant="outline">
							{t("save")}
						</Button>
						<Button disabled={!initialXml} onClick={saveAndExit}>
							{t("save-and-exit")}
						</Button>
					</div>
				</FormHeaderBase>
				<div className="min-h-0 flex-1">
					{loadError ? (
						<DiagramError diagramName="BPMN" error={loadError} />
					) : (
						<Suspense fallback={<div className="h-full w-full bg-[var(--color-diagram-bg)]" />}>
							{initialXml === null ? (
								<div className="h-full w-full bg-[var(--color-diagram-bg)]" />
							) : (
								<LazyBpmnModelerCanvas onChange={onChange} onReady={onReady} xml={initialXml} />
							)}
						</Suspense>
					)}
				</div>
			</div>
			{showWarning && (
				<UnsavedChangesModal isOpen={showWarning} onDontSave={exit} onOpenChange={stay} onSave={saveAndExit} />
			)}
		</>
	);
};

export default BpmnEditor;
